import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, Search, Phone, Film, Video, Camera } from 'lucide-react';
import Seo from '@/components/Seo';

export default function PageNotFound() {
  return (
    <>
      <Seo 
        title="Page Not Found | Videoed" 
        description="The page you're looking for cannot be found. Return to Videoed homepage for video camera hire, teleprompter services, and production crew." 
        canonical="https://videoed.co.uk/404" 
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 relative overflow-hidden">
        {/* Animated background blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <motion.div
            animate={{ y: [0, -30, 0], x: [0, 20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            className="absolute top-20 left-10 w-64 h-64 bg-purple-200/30 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ y: [0, 40, 0], x: [0, -30, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            className="absolute bottom-20 right-10 w-72 h-72 bg-pink-200/30 rounded-full blur-3xl"
          />
          <motion.div
            animate={{ y: [0, -25, 0], x: [0, 25, 0] }}
            transition={{ duration: 9, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
            className="absolute top-1/2 left-1/2 w-80 h-80 bg-blue-200/30 rounded-full blur-3xl"
          />
        </div>

        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* 404 Header */}
            <motion.div
              initial={{ opacity: 0, y: -30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mb-8"
            >
              <h1 className="text-9xl md:text-[12rem] font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 leading-none">
                404
              </h1>
            </motion.div>

            {/* Icon with wobble animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-8"
            >
              <motion.div
                animate={{ y: [0, -8, 0], rotate: [0, 5, 0, -5, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="inline-flex items-center justify-center w-24 h-24 bg-white rounded-3xl shadow-xl"
              >
                <Search className="w-12 h-12 text-purple-600" />
              </motion.div>
            </motion.div>

            {/* Main Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Oops! Page Not Found
              </h2>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed max-w-2xl mx-auto">
                The page you're looking for seems to have wandered off. Don't worry — we'll help you find your way back to great video content.
              </p>
            </motion.div>

            {/* Quick Links Cards */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
            >
              <Link
                to="/"
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Home className="w-7 h-7 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Home Page</h3>
                  <p className="text-sm text-gray-600">Return to our homepage</p>
                </div>
              </Link>

              <Link
                to="/camera-catalogue"
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 bg-pink-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Camera className="w-7 h-7 text-pink-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Camera Hire</h3>
                  <p className="text-sm text-gray-600">Browse our camera catalogue</p>
                </div>
              </Link>

              <Link
                to="/contact-us"
                className="group bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 bg-blue-100 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                    <Phone className="w-7 h-7 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Contact Us</h3>
                  <p className="text-sm text-gray-600">Get in touch for help</p>
                </div>
              </Link>
            </motion.div>

            {/* Popular Services */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-white rounded-3xl p-8 shadow-lg"
            >
              <h3 className="text-xl font-bold text-gray-900 mb-6">Popular Services</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { to: '/video-camera-package-hire', label: 'Camera Packages', icon: Video },
                  { to: '/drone-hire', label: 'Drone Hire', icon: Film },
                  { to: '/apple-hire', label: 'Apple Mac Hire', icon: Camera },
                  { to: '/full-production-services', label: 'Production Services', icon: Film },
                ].map((item) => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="flex flex-col items-center p-4 rounded-xl bg-gradient-to-br from-gray-50 to-gray-100 hover:from-purple-50 hover:to-pink-50 transition-all duration-300 group"
                  >
                    <item.icon className="w-6 h-6 text-gray-600 group-hover:text-purple-600 mb-2 transition-colors" />
                    <span className="text-sm font-medium text-gray-700 group-hover:text-purple-600 transition-colors">
                      {item.label}
                    </span>
                  </Link>
                ))}
              </div>
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="mt-12"
            >
              <Link
                to="/"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-full hover:from-purple-700 hover:to-pink-700 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                <Home className="w-5 h-5 mr-2" />
                Back to Homepage
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
}